import React, { useState, useEffect } from "react";
import {
  Video, ShoppingBag, Users, Crown, Film, Eye, EyeOff, Upload,
  Edit, Trash2, Plus, Package, DollarSign, Tag, Clock
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";

export default function ContentDashboard() {
  const [videos, setVideos] = useState([]);
  const [storeVideos, setStoreVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // all, members, vip, bts, store
  const [editingVideo, setEditingVideo] = useState(null);
  const [showBundleForm, setShowBundleForm] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const [catalogRes, storeRes] = await Promise.all([
        api.get("/api/catalog"),
        api.get("/api/store/videos"),
      ]);
      setVideos(catalogRes.data);
      setStoreVideos(storeRes.data);
    } catch (err) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (video, visibility) => {
    try {
      const currentVisibility = video.visibility || [];
      const newVisibility = currentVisibility.includes(visibility)
        ? currentVisibility.filter(v => v !== visibility)
        : [...currentVisibility, visibility];

      await api.put(`/api/catalog/${video.id}`, {
        ...video,
        visibility: newVisibility
      });

      toast.success(`Video ${newVisibility.includes(visibility) ? 'published to' : 'removed from'} ${visibility}`);
      loadContent();
    } catch (err) {
      toast.error("Failed to update video");
    }
  };

  const filteredVideos = videos.filter(v => {
    if (activeTab === "all") return true;
    return (v.visibility || []).includes(activeTab);
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            🎬 Content Management
          </h1>
          <p className="text-zinc-400">
            Manage all videos across Members, VIP, BTS, and Store
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <StatCard
            icon={<Video size={20} />}
            label="Total Videos"
            value={videos.length}
            color="#3b82f6"
          />
          <StatCard
            icon={<Users size={20} />}
            label="Members"
            value={videos.filter(v => (v.visibility || []).includes("members")).length}
            color="#10b981"
          />
          <StatCard
            icon={<Crown size={20} />}
            label="VIP"
            value={videos.filter(v => (v.visibility || []).includes("vip")).length}
            color="#a855f7"
          />
          <StatCard
            icon={<Film size={20} />}
            label="BTS"
            value={videos.filter(v => (v.visibility || []).includes("bts")).length}
            color="#f59e0b"
          />
          <StatCard
            icon={<ShoppingBag size={20} />}
            label="Store"
            value={storeVideos.length}
            color="#ec4899"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
          {[
            { id: "all", label: "All Videos", icon: Video },
            { id: "members", label: "Members", icon: Users },
            { id: "vip", label: "VIP", icon: Crown },
            { id: "bts", label: "BTS", icon: Film },
            { id: "store", label: "Store", icon: ShoppingBag },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setShowBundleForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
            <Package size={18} />
            Create Bundle
          </button>
        </div>

        {/* Videos Table */}
        {activeTab === "store" ? (
          <StoreVideosTable videos={storeVideos} onRefresh={loadContent} />
        ) : (
          <VideosTable
            videos={filteredVideos}
            onToggleVisibility={toggleVisibility}
            onRefresh={loadContent}
          />
        )}

        {/* Bundle Form Modal */}
        {showBundleForm && (
          <BundleForm
            onClose={() => setShowBundleForm(false)}
            onCreated={() => {
              setShowBundleForm(false);
              loadContent();
            }}
          />
        )}
      </div>
    </AppLayout>
  );
}

function VideosTable({ videos, onToggleVisibility, onRefresh }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-800/50 border-b border-white/5">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Video</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Title</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Published To</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Views</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-zinc-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                  No videos found
                </td>
              </tr>
            ) : (
              videos.map((video) => (
                <tr key={video.id} className="border-b border-white/5 hover:bg-white/5 transition">
                  <td className="px-6 py-4">
                    <img
                      src={video.thumbnail || "https://via.placeholder.com/160x90"}
                      alt={video.title}
                      className="w-32 h-18 object-cover rounded border border-white/10"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-white">{video.title}</p>
                    {video.description && (
                      <p className="text-sm text-zinc-400 line-clamp-1 mt-1">{video.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onToggleVisibility(video, "members")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                          (video.visibility || []).includes("members")
                            ? "bg-green-500/20 text-green-300 border border-green-500/30"
                            : "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                        }`}>
                        Members
                      </button>
                      <button
                        onClick={() => onToggleVisibility(video, "vip")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                          (video.visibility || []).includes("vip")
                            ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                            : "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                        }`}>
                        VIP
                      </button>
                      <button
                        onClick={() => onToggleVisibility(video, "bts")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                          (video.visibility || []).includes("bts")
                            ? "bg-orange-500/20 text-orange-300 border border-orange-500/30"
                            : "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                        }`}>
                        BTS
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-zinc-400">{video.views || 0}</td>
                  <td className="px-6 py-4 text-right">
                    <button className="p-2 text-zinc-400 hover:text-purple-400 transition">
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreVideosTable({ videos, onRefresh }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-800/50 border-b border-white/5">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Video</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Title</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Price</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Sales</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-zinc-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                  No store videos found
                </td>
              </tr>
            ) : (
              videos.map((video) => (
                <tr key={video.video_id} className="border-b border-white/5 hover:bg-white/5 transition">
                  <td className="px-6 py-4">
                    <img
                      src={video.thumbnail_url || "https://via.placeholder.com/160x90"}
                      alt={video.title}
                      className="w-32 h-18 object-cover rounded border border-white/10"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-white">{video.title}</p>
                  </td>
                  <td className="px-6 py-4 text-white font-semibold">
                    ${video.purchase_price || video.rental_price || 0}
                  </td>
                  <td className="px-6 py-4 text-zinc-400">{video.purchase_count || 0}</td>
                  <td className="px-6 py-4 text-right">
                    <button className="p-2 text-zinc-400 hover:text-purple-400 transition">
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BundleForm({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    video_ids: [],
    price: 0,
    discount_percent: 0,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/store/bundles", formData);
      toast.success("Bundle created!");
      onCreated();
    } catch (err) {
      toast.error("Failed to create bundle");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-xl max-w-lg w-full">
        <div className="p-6 border-b border-white/5">
          <h3 className="text-xl font-bold text-white">Create Video Bundle</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Bundle Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              placeholder="Summer Collection"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              rows="3"
              placeholder="Bundle description..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Bundle Price</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Discount %</label>
            <input
              type="number"
              value={formData.discount_percent}
              onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              min="0"
              max="100"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
              Create Bundle
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-2">
        <div style={{ color }}>{icon}</div>
        <span className="text-2xl font-bold text-white">{value}</span>
      </div>
      <div className="text-sm text-zinc-400">{label}</div>
    </div>
  );
}
